import React from 'react';

function getSizes(w, h) {
  const portrait = h > w;

  // Desktop / large tablet
  if (w > 768) return { sq: 20, gap: 3, pad: 4, bgap: 8 };

  // Derive square size from available height so bars always fit.
  //
  // A TenBar is 10 squares tall.
  // Bar height = 10*sq + 9*gap + 2*pad
  //            = sq*(10 + 9*0.22 + 2*0.28) ≈ sq * 12.5
  //
  // Overhead ≈ 380px portrait, 300px landscape (same as NumberBlocks).
  // Number label below bars ≈ 1.3*sq → total factor ≈ 13.8

  let sq;
  if (portrait) {
    const available = Math.max(60, h - 380);
    sq = Math.max(8, Math.min(18, Math.floor(available / 13.8)));
  } else {
    const available = Math.max(60, h - 300);
    sq = Math.max(8, Math.min(14, Math.floor(available / 13.8)));
  }

  const gap  = Math.max(2, Math.round(sq * 0.22));
  const pad  = Math.max(2, Math.round(sq * 0.28));
  const bgap = Math.max(4, Math.round(sq * 0.5));
  return { sq, gap, pad, bgap };
}

function useSizes() {
  const [sizes, setSizes] = React.useState(() => getSizes(window.innerWidth, window.innerHeight));
  React.useEffect(() => {
    const onResize = () => setSizes(getSizes(window.innerWidth, window.innerHeight));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return sizes;
}

function TenBar({ color, s }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: s.gap,
      backgroundColor: 'rgba(255,255,255,0.2)',
      border: '2px solid rgba(255,255,255,0.6)',
      borderRadius: 6,
      padding: s.pad,
    }}>
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} style={{
          width: s.sq,
          height: s.sq,
          borderRadius: 3,
          backgroundColor: color,
          border: '2px solid rgba(255,255,255,0.8)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      ))}
    </div>
  );
}

function OnesBar({ count, color, s }) {
  if (count === 0) return null;
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: s.gap,
      backgroundColor: 'rgba(255,255,255,0.1)',
      border: '2px dashed rgba(255,255,255,0.5)',
      borderRadius: 6,
      padding: s.pad,
      justifyContent: 'flex-end',
      minHeight: 10 * (s.sq + s.gap) + s.pad * 2 - s.gap,
    }}>
      {Array.from({ length: 10 }).map((_, i) => {
        const filled = i >= (10 - count);
        return (
          <div key={i} style={{
            width: s.sq,
            height: s.sq,
            borderRadius: 3,
            backgroundColor: filled ? color : 'rgba(255,255,255,0.1)',
            border: `2px solid ${filled ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.2)'}`,
            boxShadow: filled ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
          }} />
        );
      })}
    </div>
  );
}

export default function TallBlocks({ value, tenColor, oneColor }) {
  const tens = Math.floor(value / 10);
  const ones = value % 10;
  const s = useSizes();

  const tColor = tenColor || '#FF6B6B';
  const oColor = oneColor || '#FF9F43';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: s.bgap }}>
        {Array.from({ length: tens }).map((_, i) => (
          <TenBar key={i} color={tColor} s={s} />
        ))}
        {ones > 0 && <OnesBar count={ones} color={oColor} s={s} />}
        {value === 0 && (
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 20 }}>0</div>
        )}
      </div>
      <div style={{
        fontSize: s.sq * 1.3,
        fontWeight: 900,
        color: '#fff',
        textShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}>
        {value}
      </div>
    </div>
  );
}
